/**
 * Zenwise Cart Drawer - Standalone JavaScript
 * No dependencies on theme code
 */

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    get freeShippingThreshold() {
      return window.zenwiseCartSettings?.freeShippingThreshold || 50.0;
    },
    get shippingQualifiedText() {
      return (
        window.zenwiseCartSettings?.shippingQualifiedText ||
        "You've unlocked all discounts!"
      );
    },
    get subscriptionGiftEnabled() {
      return window.zenwiseCartSettings?.subscriptionGiftEnabled || false;
    },
    get subscriptionGiftCondition() {
      return window.zenwiseCartSettings?.subscriptionGiftCondition || 'exclude_bundles';
    },
    get subscriptionGiftVariantId() {
      return window.zenwiseCartSettings?.subscriptionGiftVariantId || null;
    },
    get promo1VariantId() {
      return window.zenwiseCartSettings?.promo1VariantId || null;
    },
    get promo1Threshold() {
      return Number(window.zenwiseCartSettings?.promo1Threshold) || 0;
    },
    get promo2VariantId() {
      return window.zenwiseCartSettings?.promo2VariantId || null;
    },
    get promo2Threshold() {
      return Number(window.zenwiseCartSettings?.promo2Threshold) || 0;
    },
    get showSimpleProgress() {
      return window.zenwiseCartSettings?.showSimpleProgress || false;
    },
    get simpleProgressThreshold() {
      return Number(window.zenwiseCartSettings?.simpleProgressThreshold) || 0;
    },
    get simpleProgressEnablePopup() {
      return window.zenwiseCartSettings?.simpleProgressEnablePopup || false;
    },
    get simpleProgressPopupTrigger() {
      return window.zenwiseCartSettings?.simpleProgressPopupTrigger || 'subtotal';
    },
    get simpleProgressPopupThreshold() {
      return Number(window.zenwiseCartSettings?.simpleProgressPopupThreshold) || 0;
    },
    cartDrawerId: "ZwCartDrawer",
    overlayId: "ZwCartOverlay",
    closeButtonId: "ZwCartClose",
    formId: "ZwCartForm",
    emptyCartId: "ZwCartEmpty",
  };

  // Selectors
  const SELECTORS = {
    drawer: `#${CONFIG.cartDrawerId}`,
    overlay: `#${CONFIG.overlayId}`,
    closeButton: `#${CONFIG.closeButtonId}`,
    form: `#${CONFIG.formId}`,
    emptyCart: `#${CONFIG.emptyCartId}`,
    items: "[data-zw-cart-items]",
    count: "[data-zw-cart-count]",
    subtotal: "[data-zw-subtotal]",
    shippingFill: "[data-zw-shipping-fill]",
    shippingText: "[data-zw-shipping-text]",
    shippingAmount: "[data-zw-shipping-amount]",
    shippingProgress: ".zw-shipping-progress",
    cartHeader: ".zw-cart-header",
    errorContainer: "[data-zw-error-container]",
    recommendationsGrid: "[data-zw-recommendations-grid]",
    recommendationsRoot: "[data-zw-recommendations]",
    qtyMinus: "[data-zw-qty-minus]",
    qtyPlus: "[data-zw-qty-plus]",
    qtyInput: "[data-zw-qty-input]",
    removeBtn: "[data-zw-remove]",
    trigger: ".js-drawer-open-zenwise",
  };

  // Returns the expected free qty for a given main qty based on the stored tier map.
  // Finds the highest tier whose paid qty is <= currentQty (e.g. qty=2 with map {1:0,2:1,3:2} → 1).
  function getBundleTierFreeQty(freeMap, currentQty) {
    const tiers = Object.keys(freeMap).map(Number).sort((a, b) => a - b);
    let expected = 0;
    for (const tier of tiers) {
      if (currentQty >= tier) expected = freeMap[tier];
    }
    return expected;
  }

  class ZenwiseCartDrawer {
    constructor() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) {
        console.warn("Zenwise Cart Drawer not found");
        return;
      }

      this.overlay = document.querySelector(SELECTORS.overlay);
      this.closeButton = document.querySelector(SELECTORS.closeButton);
      this.form = document.querySelector(SELECTORS.form);
      this.emptyCart = document.querySelector(SELECTORS.emptyCart);
      this.itemsContainer = document.querySelector(SELECTORS.items);
      this.loadingEl = this.drawer.querySelector("[data-zw-cart-loading]");

      this.isOpen = false;
      this.isUpdating = false;
      this.isCartRefreshing = false;
      this._giftAddFailed = false;
      this.upsellSwiper = null;
      this.originalShippingHTML = null;

      this.init();
    }

    init() {
      if (this.closeButton) {
        this.closeButton.addEventListener("click", () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener("click", () => this.close());
      }

      this.setupQuantityControls();
      this.setupRemoveButtons();
      this.setupTriggerButtons();
      this.setupCustomRecommendations();
      this.setupSubscriptionToggle();
      this.setupCheckoutInterceptor();
      this.setupProgressPopup();

      document.addEventListener("zenwise:cart:added", () => {
        if (this.isOpen) {
          this.updateCart();
        }
        this.open();
      });
      this.setLoading(false);
      this.updateCart();
    }

    setLoading(isLoading) {
      if (!this.loadingEl) return;

      if (!this.isOpen) {
        this.loadingEl.classList.remove("is-active");
        this.loadingEl.setAttribute("aria-hidden", "true");
        return;
      }

      if (isLoading) {
        this.loadingEl.classList.add("is-active");
        this.loadingEl.setAttribute("aria-hidden", "false");
      } else {
        this.loadingEl.classList.remove("is-active");
        this.loadingEl.setAttribute("aria-hidden", "true");
      }
    }

    open() {
      if (this.isOpen) return;
      this.drawer.classList.add("zw-cart-open");
      document.body.classList.add("zw-cart-no-scroll");
      this.isOpen = true;
      document.dispatchEvent(new CustomEvent("zenwise:cart:opened"));
      this.updateCart();
    }

    close() {
      if (!this.isOpen) return;
      this.drawer.classList.remove("zw-cart-open");
      document.body.classList.remove("zw-cart-no-scroll");
      this.isOpen = false;
      document.dispatchEvent(new CustomEvent("zenwise:cart:closed"));
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    setupQuantityControls() {
      document.addEventListener("click", (e) => {
        const plusBtn = e.target.closest(SELECTORS.qtyPlus);
        if (plusBtn) {
          e.preventDefault();
          const key = plusBtn.dataset.zwQtyPlus;
          this.changeQuantity(key, 1);
          return;
        }

        const minusBtn = e.target.closest(SELECTORS.qtyMinus);
        if (minusBtn) {
          e.preventDefault();
          const key = minusBtn.dataset.zwQtyMinus;
          this.changeQuantity(key, -1);
          return;
        }
      });

      document.addEventListener("change", (e) => {
        const input = e.target.closest(SELECTORS.qtyInput);
        if (input) {
          const key = input.dataset.zwQtyInput;
          let newQty = Math.max(0, parseInt(input.value) || 0);

          // For bundle items, ensure quantity is a multiple of 3
          // if (this.isBundle(key) && newQty > 0) {
          //   // Round to nearest multiple of 3 (minimum 3)
          //   newQty = Math.max(3, Math.round(newQty / 3) * 3);
          //   input.value = newQty;
          // }

          this.updateLineItem(key, newQty);
        }
      });
    }

    setupRemoveButtons() {
      document.addEventListener("click", (e) => {
        const removeBtn = e.target.closest(SELECTORS.removeBtn);
        if (removeBtn) {
          e.preventDefault();
          const key = removeBtn.dataset.zwRemove;
          this.removeItem(key);
        }
      });
    }

    setupSubscriptionToggle() {
      document.addEventListener("change", (e) => {
        const radio = e.target.closest('[data-zw-plan-radio]');
        if (!radio) return;

        const toggleEl = radio.closest('[data-zw-sub-toggle]');
        if (!toggleEl) return;

        const key = toggleEl.dataset.itemKey;
        const variantId = parseInt(toggleEl.dataset.variantId, 10);
        const productId = toggleEl.dataset.productId;
        const qty = parseInt(toggleEl.dataset.qty, 10) || 1;
        const mode = radio.value;

        const mapEl = document.getElementById('zw-selling-plan-map');
        let sellingPlanId = null;
        let freeGiftVariantIds = [];

        if (mapEl) {
          try {
            const planMap = JSON.parse(mapEl.textContent);
            const entry = planMap[productId];
            if (entry && entry.sellingPlanId) {
              sellingPlanId = entry.sellingPlanId;
            }
            if (entry && Array.isArray(entry.freeGiftVariantIds)) {
              freeGiftVariantIds = entry.freeGiftVariantIds.map(Number).filter(Boolean);
            }
          } catch(e) {}
        }

        if (!sellingPlanId) {
          const attrPlanId = toggleEl.dataset.sellingPlanId;
          if (attrPlanId && attrPlanId !== 'null' && attrPlanId !== '') {
            sellingPlanId = attrPlanId;
          }
        }

        if (freeGiftVariantIds.length === 0 && toggleEl.dataset.freeGiftIds) {
          freeGiftVariantIds = toggleEl.dataset.freeGiftIds.split(',').map((s) => parseInt(s.trim(), 10)).filter(Boolean);
        }

        const bundleId = toggleEl.dataset.bundleId || null;

        this.switchSubscriptionMode(key, variantId, qty, mode, sellingPlanId, { freeGiftVariantIds, bundleId });
      });
    }

    async switchSubscriptionMode(key, variantId, qty, mode, sellingPlanId, options = {}) {
      if (this.isUpdating) return;
      this.isUpdating = true;
      this.setLoading(true);

      try {
        let cart = this.latestCart;
        if (!cart || !cart.items) {
          try {
            const res = await fetch('/cart.js');
            if (res.ok) cart = await res.json();
          } catch (e) {}
        }

        const currentItem = cart?.items?.find((i) => i.key === key);
        const giftVariantIds = (options.freeGiftVariantIds || []).map(Number).filter(Boolean);
        const existingBundleId = options.bundleId || currentItem?.properties?.['_bundle-id'] || null;

        if (mode === 'subscribe') {
          let bundleId = existingBundleId;
          if (giftVariantIds.length > 0 && !bundleId) {
            bundleId = Math.floor(100000 + Math.random() * 900000).toString();
          }

          const mainProps = Object.assign({}, currentItem?.properties || {});
          if (bundleId && giftVariantIds.length > 0) {
            mainProps['_bundle-id'] = bundleId;
            mainProps['Bundle'] = 'Main';
          } else {
            delete mainProps['_bundle-id'];
            delete mainProps['Bundle'];
          }

          // Remove the existing item from cart
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });

          // Add the item back in subscription
          const itemsToAdd = [];
          const mainPayload = {
            id: variantId,
            quantity: qty
          };
          if (sellingPlanId) {
            mainPayload.selling_plan = parseInt(sellingPlanId, 10);
          }
          if (Object.keys(mainProps).length > 0) {
            mainPayload.properties = mainProps;
          }
          itemsToAdd.push(mainPayload);

          // If this product has free gifts on subscription, add them if not already present
          if (bundleId && giftVariantIds.length > 0) {
            giftVariantIds.forEach((giftVarId) => {
              const alreadyPresent = cart?.items?.some(
                (i) => i.variant_id === giftVarId && i.properties?.['_bundle-id'] === bundleId && i.properties?.Bundle === 'Gift'
              );
              if (!alreadyPresent) {
                itemsToAdd.push({
                  id: giftVarId,
                  quantity: 1,
                  properties: {
                    '_bundle-id': bundleId,
                    'Bundle': 'Gift'
                  }
                });
              }
            });
          }

          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });
        } else {
          // mode === 'onetime'
          // Remove the item and any associated bundle gifts from cart
          const updates = {};
          updates[key] = 0;

          if (cart?.items) {
            cart.items.forEach((item) => {
              const isLinkedGift = existingBundleId && item.properties?.['_bundle-id'] === existingBundleId && item.properties?.Bundle === 'Gift';
              const isMatchingGiftVariant = giftVariantIds.includes(item.variant_id) && item.properties?.Bundle === 'Gift';
              if (isLinkedGift || isMatchingGiftVariant) {
                updates[item.key] = 0;
              }
            });
          }

          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });

          // Add the item back as one-time without bundle properties
          const cleanProps = Object.assign({}, currentItem?.properties || {});
          delete cleanProps['_bundle-id'];
          delete cleanProps['Bundle'];

          const addPayload = { id: variantId, quantity: qty };
          if (Object.keys(cleanProps).length > 0) {
            addPayload.properties = cleanProps;
          }

          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(addPayload)
          });
        }

        await this.updateCart();
      } catch(err) {
        console.error('switchSubscriptionMode error:', err);
      } finally {
        this.isUpdating = false;
        this.setLoading(false);
      }
    }

    setupTriggerButtons() {
      const triggers = document.querySelectorAll(SELECTORS.trigger);
      triggers.forEach((trigger) => {
        trigger.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.open();
          },
          true,
        );
      });
    }

    /**
     * Check if an item is a bundle product based on its properties
     */
    isBundle(key) {
      const itemEl = document.querySelector(`[data-zw-item-key="${key}"]`);
      if (!itemEl) return false;
      return itemEl.dataset.zwIsBundle === "true";
    }

    /**
     * Get the quantity step for an item (3 for bundles, 1 for regular items)
     */
    getQuantityStep(key) {
      return this.isBundle(key) ? 3 : 1;
    }

    changeQuantity(key, delta) {
      const input = document.querySelector(`[data-zw-qty-input="${key}"]`);
      if (!input) return;

      const currentQty = parseInt(input.value) || 0;
      const step = this.getQuantityStep(key);

      // For bundles, delta is multiplied by 3
      const actualDelta = delta * step;
      const newQty = Math.max(0, currentQty + actualDelta);

      input.value = newQty;
      this.updateLineItem(key, newQty);
    }

    removeItem(key) {
      this.updateLineItem(key, 0);
    }

    setCartQuantityDisabled(disabled) {
      if (!this.itemsContainer) return;

      const buttons = this.itemsContainer.querySelectorAll(".zw-qty-btn");
      buttons.forEach((btn) => {
        if (disabled) {
          btn.setAttribute("disabled", "disabled");
          btn.classList.add("zw-qty-btn--disabled");
        } else {
          if (!btn.classList.contains("zw-qty-btn--locked")) {
            btn.removeAttribute("disabled");
            btn.classList.remove("zw-qty-btn--disabled");
          }
        }
      });
    }

    updateLineItem(key, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;
      this.setLoading(true);

      this.setCartQuantityDisabled(true);
      this.hideError(); // Clear any existing error

      const data = {
        id: key,
        quantity: quantity,
      };

      fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(data),
      })
        .then((response) => {
          // Check if response is not ok (e.g., 422 error)
          if (!response.ok) {
            return response.json().then((errorData) => {
              throw { status: response.status, ...errorData };
            });
          }
          return response.json();
        })
        .then((cart) => {
          this.updateCart().then(() => {
            this.isUpdating = false;
            this.setCartQuantityDisabled(false);
            this.setLoading(false);

            document.dispatchEvent(
              new CustomEvent("zenwise:cart:updated", {
                detail: { cart },
              }),
            );
          });
        })
        .catch((error) => {
          this.isUpdating = false;
          this.setCartQuantityDisabled(false);

          // Handle 422 error with message from Shopify
          if (error.status === 422 && error.message) {
            this.showError(error.message);
          } else if (error.description) {
            this.showError(error.description);
          } else {
            console.error("Error updating cart:", error);
            this.showError("Failed to update cart");
          }

          // Refresh cart to restore correct quantities
          this.updateCart();
        });
    }

    /**
     * Fetch cart and also build a map of compare_at_price per variant
     */
    async updateCart() {
      try {
        this.setLoading(true);

        const response = await fetch("/cart.js");
        let cart = await response.json();

        const bundlesCleaned = await this.validateBundles(cart);
        const giftChanged = await this.validateSubscriptionGift(cart);
        const promoChanged = await this.validatePromoGifts(cart);
        let freeGiftCleaned = await this.validateFreeGiftTriggers(cart);

        if (bundlesCleaned || giftChanged || promoChanged || freeGiftCleaned) {
          const refreshed = await fetch("/cart.js");
          cart = await refreshed.json();
          // Re-run free gift validation on refreshed cart in case bundle cleanup
          // removed same-variant items that were masking orphaned GWP gifts
          if (bundlesCleaned) {
            const secondPass = await this.validateFreeGiftTriggers(cart);
            if (secondPass) {
              const refreshed2 = await fetch("/cart.js");
              cart = await refreshed2.json();
            }
          }
        }

        try {
          const sectionRes = await fetch('/?sections=zenwise-cart-drawer');
          if (sectionRes.ok) {
            const sectionData = await sectionRes.json();
            if (sectionData && sectionData['zenwise-cart-drawer']) {
              const doc = new DOMParser().parseFromString(sectionData['zenwise-cart-drawer'], 'text/html');
              const newMapEl = doc.getElementById('zw-selling-plan-map');
              if (newMapEl) {
                const currentMapEl = document.getElementById('zw-selling-plan-map');
                if (currentMapEl) {
                  currentMapEl.textContent = newMapEl.textContent;
                }
              }
            }
          }
        } catch(e) {}

        const { compareMap, productSellingPlanMap } = await this.fetchCompareAtPrices(cart);

        this.updateSellingPlanMap(cart, productSellingPlanMap);

        this.latestCart = cart;
        this.updateCartCount(cart.item_count);
        this.updateSubtotal(cart.total_price);
        this.updateShippingProgress(cart.total_price, cart.items);
        this.updateSimpleProgress(cart);
        this.updateCustomRecommendations(cart);
        this.renderCartItems(cart, compareMap);
        this.loadRecommendations(cart);
        this.toggleEmptyState(cart.item_count === 0);

        this.setLoading(false);
        return cart;
      } catch (error) {
        console.error("Error fetching cart:", error);
        this.setLoading(false);
        return null;
      }
    }

    updateSellingPlanMap(cart, productSellingPlanMap = {}) {
      if (!cart || !cart.items) return;
      const mapEl = document.getElementById('zw-selling-plan-map');
      if (!mapEl) return;

      let existing = {};
      try { existing = JSON.parse(mapEl.textContent); } catch(e) {}

      cart.items.forEach((item) => {
        const pid = String(item.product_id);
        const prev = existing[pid] || {};
        const fetchedPlanId = productSellingPlanMap[pid] || null;
        existing[pid] = {
          variantId: item.variant_id,
          qty: item.quantity,
          sellingPlanId: prev.sellingPlanId || fetchedPlanId || null,
          currentPlanId: item.selling_plan_allocation
            ? item.selling_plan_allocation.selling_plan.id
            : null,
          freeGiftVariantIds: prev.freeGiftVariantIds || []
        };
      });

      mapEl.textContent = JSON.stringify(existing);
    }

    async fetchCompareAtPrices(cart) {
      const compareMap = {};
      const productSellingPlanMap = {};
      if (!cart || !cart.items || !cart.items.length) return { compareMap, productSellingPlanMap };

      const handleToProductId = {};
      cart.items.forEach((item) => {
        if (item.handle) handleToProductId[item.handle] = String(item.product_id);
      });

      const handles = Array.from(
        new Set(cart.items.map((item) => item.handle)),
      );

      await Promise.all(
        handles.map(async (handle) => {
          if (!handle) return;
          try {
            const res = await fetch(`/products/${handle}.js`);
            if (!res.ok) return;
            const product = await res.json();
            if (!product || !product.variants) return;

            product.variants.forEach((v) => {
              compareMap[v.id] = {
                compare_at_price: v.compare_at_price,
                price: v.price
              };
            });

            const pid = handleToProductId[handle];
            if (pid && product.selling_plan_groups && product.selling_plan_groups.length > 0) {
              const firstGroup = product.selling_plan_groups[0];
              if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
                productSellingPlanMap[pid] = firstGroup.selling_plans[0].id;
              }
            }
          } catch (e) {
            console.error("Error fetching product JSON for handle:", handle, e);
          }
        }),
      );

      return { compareMap, productSellingPlanMap };
    }

    updateCartCount(count) {
      // Drawer count text (e.g., "3 item(s)")
      const countElements = document.querySelectorAll(SELECTORS.count);
      countElements.forEach((el) => {
        el.textContent = `${count} item(s)`;
      });

      // Header cart bubble(s)
      const bubbles = document.querySelectorAll("[data-cart-count]");
      bubbles.forEach((bubble) => {
        bubble.textContent = count;

        if (count > 0) {
          // show bubble using theme’s CSS
          bubble.classList.add("cart-link__bubble--visible");
        } else {
          bubble.classList.remove("cart-link__bubble--visible");
        }
      });
    }

    updateSubtotal(totalPrice) {
      const subtotalElements = document.querySelectorAll(SELECTORS.subtotal);
      const formattedPrice = this.formatMoney(totalPrice);

      subtotalElements.forEach((el) => {
        el.textContent = formattedPrice;
      });
    }

    updateShippingProgress(totalPrice, items = []) {
      const container = document.querySelector("[data-cart-milestone-progress]");
      if (!container) return;

      const jsonEl = container.querySelector("[data-cart-milestones-json]");
      let milestones = [];
      try {
        milestones = JSON.parse(jsonEl?.textContent || "[]");
      } catch (e) {
        console.error("Cart milestones JSON parse error:", e);
        return;
      }
      if (!milestones.length) return;

      const positions = [30, 65, 100];
      const fillEl = container.querySelector("[data-cart-milestone-fill]");
      const unlockTextEl = container.querySelector(
        "[data-cart-milestone-unlock-text]",
      );

      const hasSubscription = items.some((item) => item.selling_plan_allocation != null);

      // Pick threshold set based on order type
      const getThreshold = (m) =>
        hasSubscription ? (m.subscription_threshold ?? m.threshold) : m.threshold;

      // Determine next unreached milestone
      let nextMilestone = null;
      for (const m of milestones) {
        if (totalPrice < getThreshold(m)) {
          nextMilestone = m;
          break;
        }
      }

      // Update unlock text
      if (unlockTextEl) {
        if (nextMilestone) {
          const remainingCents = getThreshold(nextMilestone) - totalPrice;
          const formatted = this.formatMoney(remainingCents);
          unlockTextEl.innerHTML = `Spend <strong>${formatted}</strong> more to unlock <strong>${nextMilestone.label}</strong>`;
        } else {
          unlockTextEl.innerHTML = `<strong>${CONFIG.shippingQualifiedText}</strong>`;
        }
      }

      // Update progress fill
      if (fillEl) {
        let pct = 0;
        if (!nextMilestone) {
          pct = 100;
        } else {
          const nextIdx = milestones.indexOf(nextMilestone);
          const prevIdx = nextIdx - 1;
          const prevPos = prevIdx >= 0 ? positions[prevIdx] : 0;
          const nextPos = positions[nextIdx];
          const prevThreshold = prevIdx >= 0 ? getThreshold(milestones[prevIdx]) : 0;
          const range = getThreshold(nextMilestone) - prevThreshold;
          const within = range > 0 ? (totalPrice - prevThreshold) / range : 0;
          pct = prevPos + Math.max(0, Math.min(1, within)) * (nextPos - prevPos);
        }
        fillEl.style.width = `${Math.max(0, Math.min(100, pct))}%`;
      }

      // Toggle active class on milestone dots and update their value labels
      container.querySelectorAll("[data-milestone-step]").forEach((el) => {
        const step = parseInt(el.dataset.milestoneStep, 10);
        const m = milestones[step - 1];
        if (!m) return;
        el.classList.toggle("active", totalPrice >= getThreshold(m));

        const valueEl = el.querySelector(".cart-milestone-progress__value");
        if (valueEl) {
          const dollars = getThreshold(m) / 100;
          valueEl.textContent = `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
        }
      });
    }

    isItem100PercentDiscounted(item) {
      if (!item) return false;
      if (item.final_line_price === 0 || item.price === 0 || item.line_price === 0) return true;

      if (item.properties) {
        const props = item.properties;
        if (props._form_gift === "true" || props._form_gift === true) return true;
        if (props.product === "gift") return true;
        if (props.Bundle === "Gift") return true;
        if (props._free_gift === "true" || props._free_gift === true || props["_free-gift-trigger"]) return true;
        if (props._100_discount || props["100% Discount"] || props._discount_100 || props.discount === "100%") return true;

        for (const key in props) {
          const k = String(key).toLowerCase();
          const v = String(props[key]).toLowerCase();
          if (k.includes("100%") || k.includes("free_gift") || v.includes("100%")) {
            return true;
          }
        }
      }
      return false;
    }

    updateSimpleProgress(cart) {
      const container = document.querySelector("[data-cart-simple-progress]");
      if (!container) return;

      const thresholdDollars = parseFloat(container.dataset.threshold) || 0;
      const thresholdCents = Math.round(thresholdDollars * 100);
      const messageTemplate = container.dataset.message || "Spend [amount] more to receive a free travel pouch";
      const unlockedMessage = container.dataset.unlockedMessage || "You've earned a free travel pouch!";

      if (!cart || !cart.items || cart.items.length === 0) {
        container.style.display = "none";
        return;
      }
      container.style.display = "";

      let eligibleSubtotalCents = 0;
      for (const item of cart.items) {
        if (!this.isItem100PercentDiscounted(item)) {
          const itemTotal = item.final_line_price !== undefined ? item.final_line_price : (item.price * item.quantity);
          eligibleSubtotalCents += itemTotal;
        }
      }

      const fillEl = container.querySelector("[data-cart-simple-fill]");
      const textEl = container.querySelector("[data-cart-simple-text]");

      this.latestEligibleSubtotalCents = eligibleSubtotalCents;
      this.latestCart = cart;

      const remainingCents = thresholdCents - eligibleSubtotalCents;

      if (remainingCents > 0) {
        const remainingFormatted = this.formatMoney(remainingCents);
        let msg = messageTemplate;
        msg = msg.replace(/\[amount\]|\{\{\s*amount\s*\}\}|\{amount\}/gi, `<strong>${remainingFormatted}</strong>`);
        if (textEl) textEl.innerHTML = msg;

        const pct = thresholdCents > 0 ? Math.min(100, Math.max(0, (eligibleSubtotalCents / thresholdCents) * 100)) : 0;
        if (fillEl) fillEl.style.width = `${pct}%`;
      } else {
        if (textEl) textEl.innerHTML = `<strong>${unlockedMessage}</strong>`;
        if (fillEl) fillEl.style.width = "100%";
      }
    }

    setupCheckoutInterceptor() {
      if (this.form) {
        this.form.addEventListener("submit", (e) => {
          if (this.shouldInterceptCheckout()) {
            e.preventDefault();
            e.stopPropagation();
            this.openProgressPopup();
            return false;
          }
        });
      }

      document.addEventListener("click", (e) => {
        const checkoutBtn = e.target.closest("[data-zw-checkout], [name='checkout'], .zw-checkout-btn");
        if (checkoutBtn && this.drawer && this.drawer.contains(checkoutBtn)) {
          if (this.shouldInterceptCheckout()) {
            e.preventDefault();
            e.stopPropagation();
            this.openProgressPopup();
          }
        }
      });
    }

    shouldInterceptCheckout() {
      if (!CONFIG.simpleProgressEnablePopup) {
        return false;
      }

      try {
        if (sessionStorage.getItem("zw_simple_progress_popup_dismissed") === "true") {
          return false;
        }
      } catch (e) {}

      if (!this.latestCart || !this.latestCart.items || this.latestCart.items.length === 0) {
        return false;
      }

      const trigger = CONFIG.simpleProgressPopupTrigger;

      if (trigger === 'no_subscription') {
        const hasOtpItem = this.latestCart.items.some(
          (item) => item.selling_plan_allocation == null && !this.isItem100PercentDiscounted(item)
        );
        return hasOtpItem;
      }

      const thresholdDollars = CONFIG.simpleProgressPopupThreshold;
      const thresholdCents = Math.round(thresholdDollars * 100);
      if (thresholdCents <= 0) return false;

      let eligibleSubtotalCents = 0;
      if (this.latestCart && this.latestCart.items) {
        for (const item of this.latestCart.items) {
          if (!this.isItem100PercentDiscounted(item)) {
            const itemTotal =
              item.final_line_price !== undefined
                ? item.final_line_price
                : item.price * item.quantity;
            eligibleSubtotalCents += itemTotal;
          }
        }
      } else if (this.latestEligibleSubtotalCents !== undefined) {
        eligibleSubtotalCents = this.latestEligibleSubtotalCents;
      }

      return eligibleSubtotalCents < thresholdCents;
    }

    async convertOtpItemsToSubscription() {
      if (this.isUpdating) return;
      let cart = this.latestCart;
      if (!cart || !cart.items || cart.items.length === 0) {
        try {
          const res = await fetch('/cart.js');
          if (res.ok) cart = await res.json();
        } catch (e) {}
      }
      if (!cart || !cart.items || cart.items.length === 0) return;

      const mapEl = document.getElementById('zw-selling-plan-map');
      let planMap = {};
      if (mapEl) {
        try { planMap = JSON.parse(mapEl.textContent); } catch (e) {}
      }

      // Filter OTP items (exclude already subscribed items and 100% free gifts)
      const otpItems = cart.items.filter(
        (item) => item.selling_plan_allocation == null && !this.isItem100PercentDiscounted(item)
      );

      if (otpItems.length === 0) return;

      const itemsToConvert = [];
      for (const item of otpItems) {
        const pid = String(item.product_id);
        let planId = planMap[pid]?.sellingPlanId || planMap[item.product_id]?.sellingPlanId;

        if (!planId) {
          const toggleEl = this.drawer ? this.drawer.querySelector(`[data-zw-sub-toggle][data-item-key="${item.key}"]`) : null;
          if (toggleEl && toggleEl.dataset.sellingPlanId && toggleEl.dataset.sellingPlanId !== 'null' && toggleEl.dataset.sellingPlanId !== '') {
            planId = toggleEl.dataset.sellingPlanId;
          }
        }

        if (!planId && item.handle) {
          try {
            const res = await fetch(`/products/${item.handle}.js`);
            if (res.ok) {
              const pData = await res.json();
              const firstGroup = pData.selling_plan_groups?.[0];
              if (firstGroup && firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
                planId = firstGroup.selling_plans[0].id;
              }
            }
          } catch (e) {}
        }

        if (planId) {
          const mapEntry = planMap[pid] || planMap[item.product_id] || {};
          let giftIds = mapEntry.freeGiftVariantIds || [];
          if (!giftIds || giftIds.length === 0) {
            const toggleEl = this.drawer ? this.drawer.querySelector(`[data-zw-sub-toggle][data-item-key="${item.key}"]`) : null;
            if (toggleEl && toggleEl.dataset.freeGiftIds) {
              giftIds = toggleEl.dataset.freeGiftIds.split(',').map((s) => parseInt(s.trim(), 10)).filter(Boolean);
            }
          }
          itemsToConvert.push({
            key: item.key,
            variant_id: item.variant_id,
            quantity: item.quantity,
            selling_plan: parseInt(planId, 10),
            properties: item.properties || {},
            freeGiftVariantIds: (giftIds || []).map(Number).filter(Boolean)
          });
        }
      }

      if (itemsToConvert.length === 0) return;

      this.isUpdating = true;
      this.setLoading(true);

      try {
        // Remove existing OTP line items
        const updates = {};
        itemsToConvert.forEach((item) => {
          updates[item.key] = 0;
        });

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });

        // Add them back with their subscription selling plan (and bundle gifts if applicable)
        const addItems = [];
        itemsToConvert.forEach((item) => {
          const mainProps = Object.assign({}, item.properties || {});
          let bundleId = mainProps['_bundle-id'];

          if (item.freeGiftVariantIds && item.freeGiftVariantIds.length > 0) {
            if (!bundleId) {
              bundleId = Math.floor(100000 + Math.random() * 900000).toString();
            }
            mainProps['_bundle-id'] = bundleId;
            mainProps['Bundle'] = 'Main';
          }

          const payload = {
            id: item.variant_id,
            quantity: item.quantity,
            selling_plan: item.selling_plan
          };
          if (Object.keys(mainProps).length > 0) {
            payload.properties = mainProps;
          }
          addItems.push(payload);

          if (item.freeGiftVariantIds && item.freeGiftVariantIds.length > 0 && bundleId) {
            item.freeGiftVariantIds.forEach((giftVarId) => {
              addItems.push({
                id: giftVarId,
                quantity: 1,
                properties: {
                  '_bundle-id': bundleId,
                  'Bundle': 'Gift'
                }
              });
            });
          }
        });

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: addItems })
        });

        await this.updateCart();
      } catch (err) {
        console.error('convertOtpItemsToSubscription error:', err);
      } finally {
        this.isUpdating = false;
        this.setLoading(false);
      }
    }

    setupProgressPopup() {
      document.addEventListener("click", async (e) => {
        const modal = document.getElementById("ZwProgressPopupModal");
        if (!modal) return;

        if (e.target.closest("[data-zw-popup-close]")) {
          e.preventDefault();
          this.closeProgressPopup();
          return;
        }

        if (e.target.closest("[data-zw-popup-keep-shopping]")) {
          e.preventDefault();
          const trigger = CONFIG.simpleProgressPopupTrigger;
          if (trigger === 'no_subscription') {
            const btn = e.target.closest("[data-zw-popup-keep-shopping]");
            if (btn) {
              btn.disabled = true;
              btn.classList.add("is-loading");
            }
            await this.convertOtpItemsToSubscription();
            if (btn) {
              btn.disabled = false;
              btn.classList.remove("is-loading");
            }
            this.closeProgressPopup();
          } else {
            this.closeProgressPopup();
          }
          return;
        }

        if (e.target.closest("[data-zw-popup-checkout]")) {
          e.preventDefault();
          this.closeProgressPopup();
          this.proceedToCheckout();
          return;
        }
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          const modal = document.getElementById("ZwProgressPopupModal");
          if (modal && modal.classList.contains("is-open")) {
            this.closeProgressPopup();
          }
        }
      });
    }

    openProgressPopup() {
      const modal = document.getElementById("ZwProgressPopupModal");
      if (!modal) {
        this.proceedToCheckout();
        return;
      }
      modal.classList.add("is-open");
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("zw-cart-no-scroll");
    }

    closeProgressPopup() {
      try {
        sessionStorage.setItem("zw_simple_progress_popup_dismissed", "true");
      } catch (e) {}

      const modal = document.getElementById("ZwProgressPopupModal");
      if (!modal) return;
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      if (!this.isOpen) {
        document.body.classList.remove("zw-cart-no-scroll");
      }
    }

    proceedToCheckout() {
      window.location.href = "/checkout";
    }

    renderCartItems(cart, compareMap = {}) {
      if (!this.itemsContainer) return;

      if (!cart.items.length) {
        this.itemsContainer.innerHTML = "";
        return;
      }

      let html = "";
      cart.items.forEach((item) => {
        html += this.buildCartItemHTML(item, compareMap);
      });

      this.itemsContainer.innerHTML = html;
    }

    buildCartItemHTML(item, compareMap = {}) {
      const image = item.image || "";
      const title = item.product_title || "";
      const variant = item.variant_title || "";
      const quantity = item.quantity;
      const key = item.key;

      const variantId = item.variant_id;
      const variantData = compareMap[variantId] || {};
      let compareAt = 0;

      if (item.selling_plan_allocation) {
        compareAt = Number(variantData.price) || 0;
      } else {
        compareAt = Number(variantData.compare_at_price) || 0;
      }

      const finalPrice = item.final_price; // cents
      const hasCompare = compareAt > 0 && compareAt > finalPrice;

      const price = this.formatMoney(finalPrice);
      const originalPrice = hasCompare ? this.formatMoney(compareAt) : "";

      // Check if this item is a bundle (has _bundle: true property)
      const isBundle = item.properties && item.properties._bundle === true;
      const isFormGift = item.properties && (item.properties._form_gift === "true" || item.properties.product === "gift");
      const isBundleGift = item.properties && item.properties.Bundle === "Gift";
      const isSubscription = item.selling_plan_allocation != null;
      const isLocked = isBundle || isFormGift || isBundleGift;

      if (isFormGift) {
        return this.buildFormGiftHTML(item, compareMap);
      }

      return `
        <div class="zw-cart-item" data-zw-item-key="${key}" data-zw-is-bundle="${isBundle}">
          <div class="zw-item-image">
            ${isBundleGift
              ? `<div class="zw-item-image-link"><img src="${image}" alt="${title}" loading="lazy" width="80" height="80"></div>`
              : `<a href="${item.url}" class="zw-item-image-link"><img src="${image}" alt="${title}" loading="lazy" width="80" height="80"></a>`
            }
          </div>
          <div class="zw-item-details">
            <div class="zw-item-info">
              ${isBundleGift
                ? `<span class="zw-item-title">${title}</span>`
                : `<a href="${item.url}" class="zw-item-title">${title}</a>`
              }
              ${
                (() => {
                  const vLower = (variant || "").toLowerCase().trim();
                  const is30Day = vLower.includes("30 day supply") || vLower.includes("30-day supply") || vLower.includes("30 days supply");
                  return variant && variant !== "Default Title" && !is30Day
                    ? `<div class="zw-item-variant"><span class="zw-variant-option">${variant}</span></div>`
                    : "";
                })()
              }
              ${
                item.selling_plan_allocation
                  ? `<div class="zw-item-variant"><span class="zw-variant-option">${item.selling_plan_allocation.selling_plan.name}</span></div>`
                  : ""
              }
            </div>
            <div class="zw-item-pricing">
              ${isBundleGift && item.properties && item.properties._gift_note
                ? `<p class="zw-gift-redemption-note">${item.properties._gift_note}</p>`
                : ""
              }
              <div class="zw-price-wrapper">
                ${
                  (item.properties && item.properties["_free-gift-trigger"]) || isBundleGift
                    ? `
                      ${compareAt > 0 ? `<span class="zw-price zw-price-original">${this.formatMoney(compareAt)}</span>` : ""}
                      <span class="zw-price zw-price-free">FREE</span>
                    `
                    : item.final_price == 0
                      ? `<span class="zw-price free-price">Free</span>`
                      : `
                        ${originalPrice ? `<span class="zw-price zw-price-original">${originalPrice}</span>` : ""}
                        <span class="zw-price ${originalPrice ? "zw-price-sale" : ""}">${price}</span>
                      `
                }
              </div>
            </div>
            ${
              (() => {
                if (isLocked) return '';
                const mapEl = document.getElementById('zw-selling-plan-map');
                let metafieldPlanId = null;
                let freeGiftVariantIds = [];
                if (mapEl) {
                  try {
                    const planMap = JSON.parse(mapEl.textContent);
                    const entry = planMap[item.product_id];
                    if (entry && entry.sellingPlanId) metafieldPlanId = entry.sellingPlanId;
                    if (entry && Array.isArray(entry.freeGiftVariantIds)) freeGiftVariantIds = entry.freeGiftVariantIds;
                  } catch(e) {}
                }
                const hasSubPlan = item.selling_plan_allocation != null;
                const currentPlanId = hasSubPlan ? item.selling_plan_allocation.selling_plan.id : null;
                const effectivePlanId = currentPlanId || metafieldPlanId;
                if (!effectivePlanId) return '';

                const uid = item.key.replace(/:/g, '_');
                const isSubscribed = hasSubPlan;
                return `
                  <div class="zw-sub-toggle"
                    data-zw-sub-toggle
                    data-item-key="${item.key}"
                    data-variant-id="${item.variant_id}"
                    data-product-id="${item.product_id}"
                    data-selling-plan-id="${metafieldPlanId || ''}"
                    data-current-mode="${isSubscribed ? 'subscribe' : 'onetime'}"
                    data-qty="${item.quantity}"
                    data-bundle-id="${(item.properties && item.properties['_bundle-id']) || ''}"
                    data-bundle-type="${(item.properties && item.properties['Bundle']) || ''}"
                    data-free-gift-ids="${freeGiftVariantIds.join(',')}"
                  >
                    <label class="zw-sub-toggle__option ${isSubscribed ? 'zw-sub-toggle__option--active' : ''}">
                      <input type="radio" name="zw_sub_${uid}" value="subscribe" ${isSubscribed ? 'checked' : ''} data-zw-plan-radio>
                      <span>Subscribe &amp; Save</span>
                    </label>
                    <label class="zw-sub-toggle__option ${!isSubscribed ? 'zw-sub-toggle__option--active' : ''}">
                      <input type="radio" name="zw_sub_${uid}" value="onetime" ${!isSubscribed ? 'checked' : ''} data-zw-plan-radio>
                      <span>One-Time</span>
                    </label>
                  </div>
                `;
              })()
            }
            <div class="zw-item-actions">
              <div class="zw-quantity">
                <button
                  type="button"
                  class="zw-qty-btn zw-qty-minus ${isLocked ? "zw-qty-btn--locked" : ""}"
                  data-zw-qty-minus="${key}"
                  aria-label="Decrease quantity"
                  ${isLocked ? "disabled" : ""}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="2" viewBox="0 0 8 2" fill="none"
                    style="${isLocked ? "opacity: 0;" : ""}"
                  >
                    <path d="M7.45613 1.85605H0.000125051V4.673e-05H7.45613V1.85605Z" fill="#002A49"/>
                  </svg>
                </button>

                <input
                  type="number"
                  class="zw-qty-input"
                  value="${quantity}"
                  min="0"
                  data-zw-qty-input="${key}"
                  aria-label="Quantity"
                  ${isLocked ? "disabled" : ""}
                >

                <button
                  type="button"
                  class="zw-qty-btn zw-qty-plus ${isLocked ? "zw-qty-btn--locked" : ""}"
                  data-zw-qty-plus="${key}"
                  aria-label="Increase quantity"
                  ${isLocked ? "disabled" : ""}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    style="${isLocked ? "opacity: 0;" : ""}"
                  >
                    <path d="M4.51159 7.35994H2.55959V4.62394H-0.000414014V2.73594H2.55959V-5.91278e-05H4.51159V2.73594H7.07159V4.62394H4.51159V7.35994Z" fill="#002A49"/>
                  </svg>
                </button>
              </div>

              ${
                item.properties && item.properties.Bundle === "Gift"
                  ? ""
                  : `<button type="button" class="zw-item-remove" data-zw-remove="${key}" aria-label="Remove item">Remove</button>`
              }
            </div>
          </div>
        </div>
      `;
    }

    buildFormGiftHTML(item, compareMap = {}) {
      const image = item.image || "";
      const title = item.product_title || "";
      const key = item.key;
      const variantId = item.variant_id;
      const variantData = compareMap[variantId] || {};
      const compareAt = Number(variantData.compare_at_price) || Number(variantData.price) || 0;

      const isFormMembership = variantId === CONFIG.subscriptionGiftVariantId;

      return `
        <div class="zw-cart-item zw-cart-item--form-gift" data-zw-item-key="${key}">
          <div class="zw-item-image">
            <img src="${image}" alt="${title}" loading="lazy" width="80" height="80">
          </div>
          <div class="zw-item-details">
            <div class="zw-item-info">
              <span class="zw-item-title">${title}</span>
            </div>
            <div class="zw-item-pricing">
              ${isFormMembership ? `<p class="zw-gift-redemption-note">Redemption code will be sent in post-purchase email</p>` : ""}
              <div class="zw-price-wrapper">
                ${compareAt > 0 ? `<span class="zw-price zw-price-original">${this.formatMoney(compareAt)}</span>` : ""}
                <span class="zw-price zw-price-free">FREE</span>
              </div>
            </div>
            <div class="zw-item-actions">
              <div class="zw-quantity">
                <button type="button" class="zw-qty-btn zw-qty-btn--locked" disabled aria-label="Decrease quantity">
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="2" viewBox="0 0 8 2" fill="none" style="opacity: 0;">
                    <path d="M7.45613 1.85605H0.000125051V4.673e-05H7.45613V1.85605Z" fill="#002A49"/>
                  </svg>
                </button>
                <input type="number" class="zw-qty-input" value="1" min="1" aria-label="Quantity" disabled>
                <button type="button" class="zw-qty-btn zw-qty-btn--locked" disabled aria-label="Increase quantity">
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" style="opacity: 0;">
                    <path d="M4.51159 7.35994H2.55959V4.62394H-0.000414014V2.73594H2.55959V-5.91278e-05H4.51159V2.73594H7.07159V4.62394H4.51159V7.35994Z" fill="#002A49"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    loadRecommendations(cart) {
      const container = document.querySelector(SELECTORS.recommendationsGrid);
      const root = document.querySelector(SELECTORS.recommendationsRoot);
      if (!container || !root) return;

      container.innerHTML = "";
      root.style.display = "";

      if (!cart || !cart.items || !cart.items.length) {
        root.style.display = "none";
        return;
      }

      const firstProductId = cart.items[0].product_id;
      const url = `/recommendations/products.json?product_id=${firstProductId}&limit=10&intent=related`;

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          const products = data && data.products ? data.products : [];
          this.renderRecommendations(products, container, root);
        })
        .catch((error) => {
          console.error("Error loading recommendations:", error);
          root.style.display = "none";
        });
    }

    renderRecommendations(products, container, root) {
      if (!products.length) {
        root.style.display = "none";
        container.innerHTML = "";
        return;
      }

      const slidesHtml = products
        .map((product) => {
          const variant =
            product.variants && product.variants.length
              ? product.variants[0]
              : null;
          if (!variant) return "";

          const price = this.formatMoney(variant.price);
          const comparePrice =
            variant.compare_at_price && variant.compare_at_price > variant.price
              ? this.formatMoney(variant.compare_at_price)
              : "";

          const featured =
            product.featured_image ||
            (product.featured_media &&
              product.featured_media.preview_image &&
              product.featured_media.preview_image.src);

          const imageSrc =
            typeof featured === "string"
              ? featured
              : (featured && featured.src) || "";

          const variantId = variant.id;

          return `
  <div class="zw-upsell-item swiper-slide" data-variant-id="${variantId}">
    <div class="zw-upsell-image">
      <a href="${product.url}">
        <img src="${imageSrc}&width=220" alt="${product.title}" loading="lazy">
      </a>
    </div>
    <div class="zw-upsell-content">

      <div class="zw-upsell-text">
        <p class="zw-upsell-title">${product.title}</p>
        ${
          variant && variant.title && variant.title !== "Default Title"
            ? `<p class="zw-upsell-subtitle">${variant.title}</p>`
            : ""
        }
        <div class="zw-upsell-prices">
          ${
            comparePrice
              ? `<span class="zw-upsell-price zw-upsell-price--compare">${comparePrice}</span>`
              : ""
          }
          <span class="zw-upsell-price">${price}</span>
        </div>
      </div>

      <div class="zw-upsell-bottom">
        <div class="zw-quantity">
          <button type="button" class="zw-qty-btn zw-upsell-qty-minus" aria-label="Decrease quantity">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="2" viewBox="0 0 8 2" fill="none">
              <path d="M7.45613 1.85605H0.000125051V4.673e-05H7.45613V1.85605Z" fill="#002A49"/>
            </svg>
          </button>
          <input type="number" class="zw-qty-input zw-upsell-qty-input" value="1" min="1" aria-label="Quantity">
          <button type="button" class="zw-qty-btn zw-upsell-qty-plus" aria-label="Increase quantity">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M4.51159 7.35994H2.55959V4.62394H-0.000414014V2.73594H2.55959V-5.91278e-05H4.51159V2.73594H7.07159V4.62394H4.51159V7.35994Z" fill="#002A49"/>
            </svg>
          </button>
        </div>

        <button type="button" class="zw-upsell-add" data-zw-upsell-add="${variantId}">
          Add
        </button>
      </div>
    </div>
  </div>
`;
        })
        .join("");

      container.innerHTML = slidesHtml;

      this.bindUpsellQuantity(container);
      this.bindUpsellAddButtons(container);
      this.initUpsellSwiper();
    }

    bindUpsellQuantity(container) {
      const items = container.querySelectorAll(".zw-upsell-item");
      items.forEach((item) => {
        const minus = item.querySelector(".zw-upsell-qty-minus");
        const plus = item.querySelector(".zw-upsell-qty-plus");
        const input = item.querySelector(".zw-upsell-qty-input");

        if (!input) return;

        minus &&
          minus.addEventListener("click", () => {
            const current = parseInt(input.value || "1", 10);
            if (current > 1) input.value = current - 1;
          });

        plus &&
          plus.addEventListener("click", () => {
            const current = parseInt(input.value || "1", 10);
            input.value = current + 1;
          });
      });
    }

    bindUpsellAddButtons(container) {
      const buttons = container.querySelectorAll(".zw-upsell-add");
      buttons.forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const button = e.currentTarget;
          const variantId = button.dataset.zwUpsellAdd;
          const item = button.closest(".zw-upsell-item");
          const qtyInput = item
            ? item.querySelector(".zw-upsell-qty-input")
            : null;
          const quantity = qtyInput
            ? Math.max(1, parseInt(qtyInput.value || "1", 10))
            : 1;

          if (!variantId) return;

          this.addRecommendation(variantId, button, quantity);
        });
      });
    }

    initUpsellSwiper() {
      if (typeof Swiper === "undefined") {
        return;
      }

      if (this.upsellSwiper && this.upsellSwiper.destroy) {
        this.upsellSwiper.destroy(true, true);
      }

      // eslint-disable-next-line no-undef
      this.upsellSwiper = new Swiper(".zw-recommendations-swiper", {
        slidesPerView: 1.3,
        spaceBetween: 16,
        loop: false,
        pagination: false,
        navigation: false,
        breakpoints: {
          460: {
            slidesPerView: 1.5,
          },
          1024: {
            slidesPerView: 1.5,
          },
        },
      });
    }

    addRecommendation(variantId, button, quantity = 1) {
      const originalText = button.textContent;
      button.textContent = "Adding...";
      button.disabled = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
        }),
      })
        .then((response) => response.json())
        .then(() => {
          button.textContent = "✓ Added";
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);

          this.updateCart();
        })
        .catch((error) => {
          console.error("Error adding recommendation:", error);
          button.textContent = "Error";
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        });
    }

    toggleEmptyState(isEmpty) {
      if (!this.emptyCart || !this.form) return;

      const itemsEl = this.form.querySelector(".zw-cart-items");
      const footerEl = this.form.querySelector(".zw-cart-footer");
      const shippingEl = this.form.querySelector(".zw-shipping-progress");
      const recsEl = this.form.querySelector("[data-zw-recommendations]");
      const notesEl = this.form.querySelector(".zw-cart-notes");

      if (isEmpty) {
        if (itemsEl) itemsEl.style.display = "none";
        if (footerEl) footerEl.style.display = "none";
        if (shippingEl) shippingEl.style.display = "none";
        if (recsEl) recsEl.style.display = "none";
        if (notesEl) notesEl.style.display = "none";

        this.emptyCart.style.display = "flex";
      } else {
        if (itemsEl) itemsEl.style.display = "";
        if (footerEl) footerEl.style.display = "";
        if (shippingEl) shippingEl.style.display = "";
        if (recsEl) recsEl.style.display = "";
        if (notesEl) notesEl.style.display = "";

        this.emptyCart.style.display = "none";
      }
    }

    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }

    /**
     * Show error message in the cart header
     */
    showError(message) {
      console.error(message);

      // Find or create error container
      let errorContainer = document.querySelector(SELECTORS.errorContainer);
      const cartHeader = document.querySelector(SELECTORS.cartHeader);

      if (!errorContainer && cartHeader) {
        // Create error container if it doesn't exist
        errorContainer = document.createElement("div");
        errorContainer.setAttribute("data-zw-error-container", "");
        errorContainer.className = "zw-cart-error";
        // Append at the end of cart header
        cartHeader.appendChild(errorContainer);
      }

      if (errorContainer) {
        errorContainer.innerHTML = `
          <div class="zw-cart-error__content">
            <span class="zw-cart-error__message">${message}</span>
            <button type="button" class="zw-cart-error__close" aria-label="Dismiss error">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        `;
        errorContainer.style.display = "block";

        // Add click handler for close button
        const closeBtn = errorContainer.querySelector(".zw-cart-error__close");
        if (closeBtn) {
          closeBtn.addEventListener("click", () => this.hideError());
        }
      }
    }

    /**
     * Hide/remove the error message
     */
    hideError() {
      const errorContainer = document.querySelector(SELECTORS.errorContainer);
      if (errorContainer) {
        errorContainer.style.display = "none";
        errorContainer.innerHTML = "";
      }
    }

    async validateSubscriptionGift(cart) {
      if (!CONFIG.subscriptionGiftEnabled || !CONFIG.subscriptionGiftVariantId) return false;
      if (!cart || !Array.isArray(cart.items)) return false;

      const giftItem = cart.items.find(
        (item) => item.variant_id === CONFIG.subscriptionGiftVariantId && item.properties && item.properties._form_gift === "true"
      );

      const hasSubscription = cart.items.some(
        (item) => item.selling_plan_allocation != null
      );

      const bundleIdsWithGifts = new Set(
        cart.items
          .filter((item) => item.properties && item.properties["Bundle"] === "Gift" && item.properties["_bundle-id"])
          .map((item) => item.properties["_bundle-id"])
      );
      const subscriptionHasBundleGifts = cart.items.some(
        (item) =>
          item.selling_plan_allocation != null &&
          item.properties &&
          item.properties["Bundle"] === "Main" &&
          bundleIdsWithGifts.has(item.properties["_bundle-id"])
      );

      const checkBundleExclusion = CONFIG.subscriptionGiftCondition !== 'always_add';
      const shouldExclude = checkBundleExclusion && subscriptionHasBundleGifts;
      const shouldHaveGift = hasSubscription && !shouldExclude;

      if (shouldHaveGift && !giftItem) {
        if (this._giftAddFailed) return false;
        const giftWidgetEl = document.querySelector('[data-gift-widget]');
        if (giftWidgetEl) {
          const activationClass = giftWidgetEl.dataset.activationClass || 'ig-pdp-gift-widget';
          const forceActive = giftWidgetEl.dataset.forceActive === 'true';
          if (forceActive || document.body.classList.contains(activationClass)) return false;
        }

        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            id: CONFIG.subscriptionGiftVariantId,
            quantity: 1,
            selling_plan: "",
            properties: { _form_gift: "true" },
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error("[Subscription Gift] Failed to add gift:", err.description || err.message || res.status);
          this._giftAddFailed = true;
          return false;
        }
        return true;
      }

      if (!shouldHaveGift && giftItem) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
        });
        return true;
      }

      return false;
    }

    async validatePromoGifts(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;

      const totalAmount = cart.items
        .filter((item) => {
          const isGift = item.properties && (item.properties._form_gift === "true" || item.properties.product === "gift" || item.properties.Bundle === "Gift");
          return !isGift;
        })
        .reduce((sum, item) => sum + item.final_line_price, 0) / 100;

      let cartChanged = false;

      const checkPromo = async (variantId, threshold, promoName) => {
        if (!variantId || threshold <= 0) return false;

        const hasReachedThreshold = totalAmount >= threshold;
        const giftItem = cart.items.find(
          (item) => item.variant_id === variantId && item.properties && item.properties.product === "gift"
        );

        if (hasReachedThreshold && !giftItem) {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              selling_plan: "",
              properties: {
                product: "gift"
              },
            }),
          });
          if (res.ok) {
            console.log(`[Promo Gift] Added ${promoName} variant ${variantId}`);
            return true;
          } else {
            console.error(`[Promo Gift] Failed to add ${promoName}:`, res.status);
          }
        } else if (!hasReachedThreshold && giftItem) {
          const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
          });
          if (res.ok) {
            console.log(`[Promo Gift] Removed ${promoName} variant ${variantId}`);
            return true;
          }
        }
        return false;
      };

      if (await checkPromo(CONFIG.promo1VariantId, CONFIG.promo1Threshold, "Promo 1")) {
        cartChanged = true;
      }
      
      if (cartChanged) return true;

      if (await checkPromo(CONFIG.promo2VariantId, CONFIG.promo2Threshold, "Promo 2")) {
        cartChanged = true;
      }

      return cartChanged;
    }

    async validateFreeGiftTriggers(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;

      const perTriggerMode = window.theme && window.theme.settings && window.theme.settings.gwpQuantityMode === 'per_trigger';

      // Build map: _gwp-bundle-id → expected gift quantity
      const triggerMap = {};
      cart.items.forEach((item) => {
        const gwpId = item.properties && item.properties["_gwp-bundle-id"];
        if (!gwpId) return;
        triggerMap[gwpId] = perTriggerMode ? item.quantity : 1;
      });

      // Reconcile gift quantities against expected value (1 or trigger qty depending on mode)
      const updates = {};
      cart.items.forEach((item) => {
        const trigger = item.properties && item.properties["_free-gift-trigger"];
        if (!trigger) return;
        const expectedQty = triggerMap[trigger] !== undefined ? triggerMap[trigger] : 0;
        if (item.quantity !== expectedQty) {
          updates[item.key] = expectedQty;
        }
      });

      let changed = Object.keys(updates).length > 0;
      if (changed) {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ updates }),
        });
      }

      // Recover missing gifts from localStorage
      let gwpStore = {};
      try { gwpStore = JSON.parse(localStorage.getItem("_zw_gwp") || "{}"); } catch (e) {}

      let storeChanged = false;
      for (const gwpId of Object.keys(gwpStore)) {
        const triggerItem = cart.items.find(
          (item) => item.properties && item.properties["_gwp-bundle-id"] === gwpId
        );

        if (!triggerItem) {
          // Trigger gone — clean up localStorage
          delete gwpStore[gwpId];
          storeChanged = true;
          continue;
        }

        const hasGifts = cart.items.some(
          (item) => item.properties && item.properties["_free-gift-trigger"] === gwpId
        );

        if (hasGifts) {
          // Gifts confirmed in cart — no longer needed
          delete gwpStore[gwpId];
          storeChanged = true;
          continue;
        }

        // Try to add missing gifts
        for (const giftVariantId of gwpStore[gwpId]) {
          try {
            const res = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
              body: JSON.stringify({
                id: giftVariantId,
                quantity: perTriggerMode ? triggerItem.quantity : 1,
                properties: { "_free-gift-trigger": gwpId, "Bundle": "Gift" },
              }),
            });
            if (res.ok) changed = true;
          } catch (e) {
            // Still at cart limit — will retry on next updateCart
          }
        }
      }

      if (storeChanged) {
        try { localStorage.setItem("_zw_gwp", JSON.stringify(gwpStore)); } catch (e) {}
      }

      return changed;
    }

    async validateBundles(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;

      let tierStore = {};
      try { tierStore = JSON.parse(localStorage.getItem("_zw_bundle_tiers") || "{}"); } catch (e) {}

      // Map bundleId → main cart item
      const bundleMainMap = {};
      cart.items.forEach((item) => {
        if (item.properties?.Bundle === "Main" && item.properties["_bundle-id"]) {
          bundleMainMap[item.properties["_bundle-id"]] = item;
        }
      });

      const validBundleIds = new Set(Object.keys(bundleMainMap));
      const updates = {};
      // Track which bundle IDs already have a same-variant free item in cart
      const bundlesWithFreeItem = new Set();

      cart.items.forEach((item) => {
        const bundleId = item.properties?.["_bundle-id"];
        if (!bundleId || item.properties?.Bundle !== "Gift") return;

        if (!validBundleIds.has(bundleId)) {
          // Main item gone — remove gift entirely
          updates[item.key] = 0;
          return;
        }

        const mainItem = bundleMainMap[bundleId];
        // If main item is no longer in subscription:
        // Cross-product gifts (free_products_for_bundle) or tier bundles without OTP tier must be removed!
        if (mainItem && mainItem.selling_plan_allocation == null) {
          const tierData = tierStore[bundleId];
          if (item.variant_id !== mainItem.variant_id || !tierData?.freeMap) {
            updates[item.key] = 0;
            return;
          }
        }

        // Tier-based qty adjustment for same-variant free units
        const tierData = tierStore[bundleId];
        if (!tierData?.freeMap) return;

        if (!mainItem || item.variant_id !== mainItem.variant_id) return;

        bundlesWithFreeItem.add(bundleId);

        const expectedFreeQty = getBundleTierFreeQty(tierData.freeMap, mainItem.quantity);
        if (item.quantity !== expectedFreeQty) {
          updates[item.key] = expectedFreeQty;
        }
      });

      // Clean up stale localStorage entries for bundles no longer in cart
      let storeChanged = false;
      for (const bundleId of Object.keys(tierStore)) {
        if (!validBundleIds.has(bundleId)) {
          delete tierStore[bundleId];
          storeChanged = true;
        }
      }
      if (storeChanged) {
        try { localStorage.setItem("_zw_bundle_tiers", JSON.stringify(tierStore)); } catch (e) {}
      }

      let changed = Object.keys(updates).length > 0;
      if (changed) {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ updates }),
        });
      }

      // Add freebies for bundles that now qualify but don't have a free item yet
      for (const [bundleId, mainItem] of Object.entries(bundleMainMap)) {
        const tierData = tierStore[bundleId];
        if (!tierData?.freeMap) continue;
        if (bundlesWithFreeItem.has(bundleId)) continue;

        const expectedFreeQty = getBundleTierFreeQty(tierData.freeMap, mainItem.quantity);
        if (expectedFreeQty <= 0) continue;

        try {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
            body: JSON.stringify({
              id: mainItem.variant_id,
              quantity: expectedFreeQty,
              properties: { "_bundle-id": bundleId, "Bundle": "Gift" },
            }),
          });
          changed = true;
        } catch (e) {
          console.warn("[Bundle] Could not add freebie for bundle", bundleId, e);
        }
      }

      return changed;
    }

    setupCustomRecommendations() {
      this.bindCustomRecommendationsAddButtons();
      this.bindCustomRecommendationsEvents();
    }

    bindCustomRecommendationsEvents() {
      document.addEventListener("change", (e) => {
        if (e.target.matches("[data-zw-custom-rec-radio]")) {
          const card = e.target.closest(".zw-custom-rec-card");
          if (!card) return;
          const currentPriceEl = card.querySelector("[data-zw-custom-rec-current]");
          const comparePriceEl = card.querySelector("[data-zw-custom-rec-compare]");
          
          if (e.target.value === "sub") {
            if (currentPriceEl) currentPriceEl.innerHTML = card.dataset.subPrice;
            if (comparePriceEl) comparePriceEl.innerHTML = card.dataset.subCompare;
          } else {
            if (currentPriceEl) currentPriceEl.innerHTML = card.dataset.otpPrice;
            if (comparePriceEl) comparePriceEl.innerHTML = card.dataset.otpCompare;
          }
        }
      });
    }

    bindCustomRecommendationsAddButtons() {
      document.addEventListener("click", async (e) => {
        const btn = e.target.closest("[data-zw-custom-rec-add]");
        if (!btn || btn._isAdding) return;

        e.preventDefault();
        const card = btn.closest(".zw-custom-rec-card");
        const variantId = btn.dataset.variantId || (card ? card.dataset.variantId : null);
        
        let sellingPlanId = "";
        if (card) {
          const hasSub = card.dataset.hasSub === "true";
          if (hasSub) {
            const input = card.querySelector('input[type="radio"]:checked, select[data-zw-custom-rec-radio]');
            if (input && input.value === 'sub') {
              sellingPlanId = card.dataset.sellingPlanId || "";
            }
          }
        }

        if (!variantId) return;

        btn._isAdding = true;
        const originalText = btn.textContent;
        btn.textContent = "...";
        btn.disabled = true;

        try {
          const payload = {
            id: variantId,
            quantity: 1,
          };
          if (sellingPlanId && sellingPlanId.trim() !== "") {
            payload.selling_plan = sellingPlanId.trim();
          }

          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(payload),
          });

          if (res.ok) {
            await this.updateCart();
            document.dispatchEvent(new CustomEvent("zenwise:cart:added"));
          } else {
            console.error("Failed to add custom recommendation product to cart");
          }
        } catch (err) {
          console.error("Error adding custom recommendation to cart:", err);
        } finally {
          btn._isAdding = false;
          btn.textContent = originalText;
          btn.disabled = false;
        }
      });
    }

    async updateCustomRecommendations(cart) {
      const root = document.querySelector("[data-zw-custom-recommendations]");
      if (!root) return;

      const scriptEl = root.querySelector("[data-zw-custom-rec-json]");
      let candidates = [];
      try {
        candidates = JSON.parse(scriptEl?.textContent || "[]");
      } catch (e) {
        console.error("Custom recommendations JSON parse error:", e);
        return;
      }

      if (!candidates.length || !cart || !cart.items || cart.items.length === 0) {
        root.style.display = "none";
        return;
      }

      const candidateMap = new Map();
      candidates.forEach((c) => {
        candidateMap.set(Number(c.id), c);
      });

      const cartProductIds = new Set((cart?.items || []).map((i) => Number(i.product_id)));
      const cartVariantIds = new Set((cart?.items || []).map((i) => Number(i.variant_id)));

      let productsToDisplay = [];

      if (cart && cart.items && cart.items.length > 0) {
        const firstProductId = cart.items[0].product_id;
        try {
          const res = await fetch(`/recommendations/products.json?product_id=${firstProductId}&limit=10&intent=related`);
          if (res.ok) {
            const data = await res.json();
            const recProducts = data.products || [];

            recProducts.forEach((rp) => {
              const pId = Number(rp.id);
              if (candidateMap.has(pId) && !cartProductIds.has(pId)) {
                const candidateInfo = candidateMap.get(pId);
                const firstVar = rp.variants && rp.variants.length ? rp.variants[0] : null;
                const vId = firstVar ? Number(firstVar.id) : candidateInfo.variant_id;
                if (!cartVariantIds.has(vId)) {
                  const rawImg = rp.featured_image || (rp.featured_media && rp.featured_media.preview_image && rp.featured_media.preview_image.src) || candidateInfo.featured_image;
                  const featuredImg = this.formatHighResImgUrl(rawImg);
                  productsToDisplay.push({
                    id: pId,
                    title: rp.title || candidateInfo.title,
                    url: rp.url || candidateInfo.url,
                    featured_image: featuredImg,
                    otp_price: candidateInfo.otp_price,
                    otp_compare_at_price: candidateInfo.otp_compare_at_price,
                    sub_price: candidateInfo.sub_price,
                    sub_compare_at_price: candidateInfo.sub_compare_at_price,
                    has_subscription: candidateInfo.has_subscription,
                    variant_id: vId,
                    selling_plan_id: candidateInfo.selling_plan_id || "",
                  });
                }
              }
            });
          }
        } catch (err) {
          console.warn("Shopify recommendations API fetch failed, falling back to candidate list", err);
        }
      }

      const limit = parseInt(root.dataset.limit, 10) || 4;

      if (productsToDisplay.length === 0) {
        candidates.forEach((c) => {
          const pId = Number(c.id);
          const vId = Number(c.variant_id);
          if (!cartProductIds.has(pId) && !cartVariantIds.has(vId)) {
            productsToDisplay.push(c);
          }
        });
      }

      productsToDisplay = productsToDisplay.slice(0, limit);

      this.renderCustomRecommendations(productsToDisplay, root);
    }

    renderCustomRecommendations(products, root) {
      if (!products || !products.length) {
        root.style.display = "none";
        return;
      }

      root.style.display = "";
      const uiPref = root.dataset.ui || "radio";

      const slidesContainer = root.querySelector("[data-zw-custom-rec-slides]");
      const swiperEl = root.querySelector(".zw-custom-recommendations-swiper");
      if (!slidesContainer || !swiperEl) return;

      if (swiperEl._zwCustomSwiper) {
        try {
          swiperEl._zwCustomSwiper.destroy(true, true);
        } catch (e) {}
        swiperEl._zwCustomSwiper = null;
      }

      const slidesHtml = products
        .map((p) => {
          const rawImg = typeof p.featured_image === "string" ? p.featured_image : (p.featured_image && p.featured_image.src ? p.featured_image.src : "");
          const imgUrl = this.formatHighResImgUrl(rawImg);
          let priceHtml = '';
          let toggleHtml = '';
          
          if (p.has_subscription) {
            priceHtml = `
              <span class="zw-custom-rec-card__price--compare" data-zw-custom-rec-compare>${p.sub_compare_at_price || ""}</span>
              <span class="zw-custom-rec-card__price--current" data-zw-custom-rec-current>${p.sub_price}</span>
            `;
            
            if (uiPref === "dropdown") {
              toggleHtml = `
                <div class="zw-custom-rec-card__purchase-type zw-custom-rec-card__purchase-type--dropdown">
                  <select class="zw-custom-rec-type-select" data-zw-custom-rec-radio>
                    <option value="sub" selected>Subscribe & Save</option>
                    <option value="otp">One-Time</option>
                  </select>
                </div>
              `;
            } else {
              toggleHtml = `
                <div class="zw-custom-rec-card__purchase-type">
                  <label class="zw-custom-rec-type-label">
                    <input type="radio" name="zw-rec-type-${p.id}" value="sub" checked data-zw-custom-rec-radio>
                    <span>Subscribe & Save</span>
                  </label>
                  <label class="zw-custom-rec-type-label">
                    <input type="radio" name="zw-rec-type-${p.id}" value="otp" data-zw-custom-rec-radio>
                    <span>One-Time</span>
                  </label>
                </div>
              `;
            }
          } else {
            priceHtml = `
              ${p.otp_compare_at_price ? `<span class="zw-custom-rec-card__price--compare">${p.otp_compare_at_price}</span>` : ""}
              <span class="zw-custom-rec-card__price--current">${p.otp_price}</span>
            `;
          }

          return `
            <div class="swiper-slide zw-custom-rec-card" 
              data-product-id="${p.id}" 
              data-variant-id="${p.variant_id}"
              data-selling-plan-id="${p.selling_plan_id || ""}"
              data-otp-price="${p.otp_price || ""}"
              data-otp-compare="${p.otp_compare_at_price || ""}"
              data-sub-price="${p.sub_price || ""}"
              data-sub-compare="${p.sub_compare_at_price || ""}"
              data-has-sub="${p.has_subscription ? "true" : "false"}"
            >
              <div class="zw-custom-rec-card__image-wrap">
                <a href="${p.url}">
                  ${imgUrl ? `<img src="${imgUrl}" alt="${p.title}" loading="lazy" width="120" height="120">` : ""}
                </a>
              </div>
              <div class="zw-custom-rec-card__info">
                <a href="${p.url}" class="zw-custom-rec-card__title">${p.title}</a>
                <div class="zw-custom-rec-card__price">
                  ${priceHtml}
                </div>
                ${toggleHtml}
              </div>
              <div class="zw-custom-rec-card__action">
                <button
                  type="button"
                  class="zw-custom-rec-card__add-btn"
                  data-zw-custom-rec-add
                  data-variant-id="${p.variant_id}"
                >
                  ADD
                </button>
              </div>
            </div>
          `;
        })
        .join("");

      slidesContainer.innerHTML = slidesHtml;

      if (typeof Swiper !== "undefined") {
        const prevEl = root.querySelector(".zw-custom-recommendations__nav--prev");
        const nextEl = root.querySelector(".zw-custom-recommendations__nav--next");
        const pagEl = root.querySelector(".zw-custom-recommendations__pagination");

        swiperEl._zwCustomSwiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 16,
          observer: true,
          observeParents: true,
          navigation: {
            nextEl: nextEl,
            prevEl: prevEl,
          },
          pagination: {
            el: pagEl,
            clickable: true,
          },
        });
      }
    }

    formatHighResImgUrl(src) {
      if (!src) return "";
      let url = typeof src === "string" ? src : (src && src.src ? src.src : "");
      if (!url) return "";
      url = url.replace(/_(small|thumb|pico|icon|compact|medium|large|100x100|200x200)\./gi, "_600x.");
      if (url.includes("width=")) {
        url = url.replace(/width=\d+/gi, "width=600");
      } else if (!url.includes("_600x.")) {
        const sep = url.includes("?") ? "&" : "?";
        url += `${sep}width=600`;
      }
      return url;
    }
  }

  let cartDrawerInstance = null;

  function initCartDrawer() {
    if (!cartDrawerInstance) {
      cartDrawerInstance = new ZenwiseCartDrawer();
    }
    return cartDrawerInstance;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCartDrawer);
  } else {
    initCartDrawer();
  }

  window.openCartDrawer = function () {
    const instance = initCartDrawer();
    if (instance) {
      instance.open();
    }
  };

  window.closeCartDrawer = function () {
    const instance = initCartDrawer();
    if (instance) {
      instance.close();
    }
  };

  window.toggleCartDrawer = function () {
    const instance = initCartDrawer();
    if (instance) {
      instance.toggle();
    }
  };

  window.refreshCartDrawer = function () {
    const instance = initCartDrawer();
    if (instance) {
      instance.updateCart();
    }
  };
})();
